import React from 'react'
import {
  list,
  text,
} from '@groovehq/internal-design-system/lib/styles/elements'
import { useSelector } from 'react-redux'
import { selectUsageForProduct } from 'ducks/billing/selectors/usage'
import { useProductFeature } from 'ducks/billing/hooks'
import { selectShowKbPlans } from 'ducks/billing/selectors/selectShowKbPlans'
import { getMaxKbsForInboxPlanId } from 'ducks/billing/util'
import { INBOX, KB } from 'ducks/billing/productTypes'
import { FEATURE_KB_MAX_KBS } from 'ducks/billing/featureTypes'
import {
  USAGE_INBOX_API_ACCESS,
  USAGE_INBOX_BRANDING,
  USAGE_INBOX_CANNED_REPLIES,
  USAGE_INBOX_CANNED_REPLIES_TIER_1,
  USAGE_INBOX_CHANNELS,
  USAGE_INBOX_CUSTOM_FIELDS,
  USAGE_INBOX_CUSTOM_PROFILE,
  USAGE_INBOX_FOLDERS,
  USAGE_INBOX_INTEGRATIONS,
  USAGE_INBOX_MAILBOXES,
  USAGE_INBOX_RULES,
  USAGE_INBOX_RULES_TIER_1,
  USAGE_INBOX_RULES_TIER_2,
  USAGE_INBOX_SATISFACTIONS_RATINGS,
  USAGE_INBOX_TEAMS,
  USAGE_INBOX_USERS,
  USAGE_INBOX_WIDGETS,
} from 'ducks/billing/usageTypes'
import { checkNumberLimit, checkBooleanLimit } from 'util/usageLimit'
import Feature from './Feature'
import { styles } from '../styles'

export default function InboxFeatures({
  className,
  maxIntegrations,
  maxCannedReplies,
  maxMailboxes,
  maxChannels,
  maxUsers,
  teams,
  apiAccess,
  satisfactionsRatings,
  maxRules,
  rulesTier1,
  rulesTier2,
  customProfile,
  premiumIntegrations,
  branding,
  maxFolders,
  customFields,
  maxWidgets,
  cannedRepliesTier1,
  newPlanId,
}) {
  const inboxUsage = useSelector(state => selectUsageForProduct(state, INBOX))
  const showKbPlans = useSelector(selectShowKbPlans)
  const {
    featureLimit: currentMaxKbsInKbPlan,
    currentUsage: kbFeatureUsage,
  } = useProductFeature(KB, FEATURE_KB_MAX_KBS)

  const maxKbs = showKbPlans
    ? 'unlimited'
    : getMaxKbsForInboxPlanId(newPlanId, currentMaxKbsInKbPlan)

  return (
    <>
      <div
        className="grui mt-10"
        css={[text.styles.textSm, text.styles.fontMedium]}
      >
        {app.t('Mailbox')}
      </div>
      <ul css={[list.styles.preflight, styles.features]} className={className}>
        {checkNumberLimit(inboxUsage[USAGE_INBOX_CHANNELS], maxChannels) && (
          <Feature>
            Remove channels (Limit of {maxChannels},{' '}
            {inboxUsage[USAGE_INBOX_CHANNELS]} in use)
          </Feature>
        )}
        {checkNumberLimit(inboxUsage[USAGE_INBOX_MAILBOXES], maxMailboxes) && (
          <Feature>
            Remove {app.t('Mailboxes')} (Limit of {maxMailboxes},{' '}
            {inboxUsage[USAGE_INBOX_MAILBOXES]} in use)
          </Feature>
        )}
        {checkNumberLimit(kbFeatureUsage, maxKbs) && (
          <Feature>
            Remove Knowledge Bases (Limit of {maxKbs}, {kbFeatureUsage} in use)
          </Feature>
        )}
        {checkNumberLimit(
          inboxUsage[USAGE_INBOX_INTEGRATIONS],
          maxIntegrations
        ) && (
          <Feature>
            Remove integrations (Limit of {maxIntegrations},{' '}
            {inboxUsage[USAGE_INBOX_INTEGRATIONS]} in use)
          </Feature>
        )}
        {checkBooleanLimit(
          inboxUsage.premiumIntegrations,
          premiumIntegrations
        ) && <Feature>Remove premium integrations</Feature>}
        {checkNumberLimit(inboxUsage[USAGE_INBOX_USERS], maxUsers) && (
          <Feature>
            Remove {app.t('agents')} (Limit of {maxUsers},{' '}
            {inboxUsage[USAGE_INBOX_USERS]} in{` `}use)
          </Feature>
        )}
        {checkBooleanLimit(inboxUsage[USAGE_INBOX_TEAMS] > 0, teams) && (
          <Feature>
            Remove {app.t('Teams')} ({inboxUsage[USAGE_INBOX_TEAMS]}{' '}
            {app.t('teams')} in use)
          </Feature>
        )}
        {checkNumberLimit(
          inboxUsage[USAGE_INBOX_CANNED_REPLIES],
          maxCannedReplies
        ) && (
          <Feature>
            Remove {app.t('canned_replies')} (Limit of {maxCannedReplies},{' '}
            {inboxUsage[USAGE_INBOX_CANNED_REPLIES]} in{` `}use)
          </Feature>
        )}
        {checkBooleanLimit(
          inboxUsage[USAGE_INBOX_CANNED_REPLIES_TIER_1],
          cannedRepliesTier1
        ) && <Feature>Remove advanced canned replies</Feature>}
        {checkNumberLimit(inboxUsage[USAGE_INBOX_RULES], maxRules) && (
          <Feature>
            Remove rules (Limit of {maxRules}, {inboxUsage[USAGE_INBOX_RULES]}{' '}
            in use)
          </Feature>
        )}
        {checkBooleanLimit(
          inboxUsage[USAGE_INBOX_RULES_TIER_1] > 0,
          rulesTier1
        ) && (
          <Feature>
            Remove advanced rules ({inboxUsage[USAGE_INBOX_RULES_TIER_1]} in
            use)
          </Feature>
        )}
        {checkBooleanLimit(
          inboxUsage[USAGE_INBOX_RULES_TIER_2] > 0,
          rulesTier2
        ) && (
          <Feature>
            Remove time based rules ({inboxUsage[USAGE_INBOX_RULES_TIER_2]} in{' '}
            use)
          </Feature>
        )}
        {checkNumberLimit(inboxUsage[USAGE_INBOX_FOLDERS], maxFolders) && (
          <Feature>
            Remove folders (Limit of {maxFolders}, {inboxUsage.folders} in use)
          </Feature>
        )}
        {checkNumberLimit(inboxUsage[USAGE_INBOX_WIDGETS], maxWidgets) && (
          <Feature>
            Remove widgets (Limit of {maxWidgets},{' '}
            {inboxUsage[USAGE_INBOX_WIDGETS]} in use)
          </Feature>
        )}
        {checkBooleanLimit(
          inboxUsage[USAGE_INBOX_CUSTOM_FIELDS],
          customFields
        ) && <Feature>Remove Custom fields</Feature>}
        {checkBooleanLimit(
          inboxUsage[USAGE_INBOX_SATISFACTIONS_RATINGS],
          satisfactionsRatings
        ) && <Feature>Disable Satisfaction ratings</Feature>}
        {checkBooleanLimit(inboxUsage[USAGE_INBOX_API_ACCESS], apiAccess) && (
          <Feature>Disable API</Feature>
        )}
        {checkBooleanLimit(
          inboxUsage[USAGE_INBOX_CUSTOM_PROFILE],
          customProfile
        ) && <Feature>Disable Custom profiles</Feature>}
        {checkBooleanLimit(inboxUsage[USAGE_INBOX_BRANDING], branding) && (
          <Feature>Enable Groove branding</Feature>
        )}
      </ul>
    </>
  )
}
